import React, { useCallback, useState } from "react";
import { inject, observer } from "mobx-react";
import BroadcastStore from "common/broadcast/_stores/broadcast/BroadcastStore";
import BroadcastStreamStore from "common/broadcast/_stores/broadcast-stream/BroadcastStreamStore";
import NodeChatStore from "common/broadcast/_stores/nodechat/NodeChatStore";
import { IShowType } from "common/broadcast/_stores/broadcast/enums";
import TailwindButton from "library/components/_tailwind/button";
import TailwindBox from "library/components/_tailwind/box";
import TailwindText from "library/components/_tailwind/text";
import TailwindInput from "library/components/_tailwind/input";
import TailwindFlex from "library/components/_tailwind/flex";
import TailwindSwitch from "library/components/_tailwind/switch";
import TailwindDropdown from "library/components/_tailwind/dropdown";
import CookieStorage from "library/core/utility/cookie-storage";
import { profileStore } from "core/stores";
import {
  FaceBlurMode,
  getFaceBlurDebugBlurAllFaces,
  setFaceBlurDebugBlurAllFaces,
} from "common/broadcast/_stores/broadcast-stream/consts";

type Props = {
  broadcastStore?: BroadcastStore;
  broadcastStreamStore?: BroadcastStreamStore;
  nodeChatStore?: NodeChatStore;
};

const QABroadcaster: React.ComponentType<Props> = ({
  broadcastStore,
  broadcastStreamStore,
  nodeChatStore,
}) => {
  const {
    setRecordingDurationInSeconds,
    recordingDurationInSeconds,
    isRecording,
  } = broadcastStore!;

  const [mockUserCount, setMockUserCount] = useState<string>("");
  const [debugBlurAllFaces, setDebugBlurAllFaces] = useState(
    getFaceBlurDebugBlurAllFaces()
  );
  const [useProfileId, setUseProfileId] = useState<boolean>(
    !!CookieStorage.get("useProfileId")
  );

  const handleIdSourceChange = useCallback((value: any) => {
    const enabled = value === "profile";
    if (enabled) {
      CookieStorage.set("useProfileId", true);
    } else {
      CookieStorage.remove("useProfileId");
    }
    setUseProfileId(enabled);
    window.location.reload();
  }, []);

  const handleDebugBlurAllFacesChange = (enabled: boolean) => {
    setFaceBlurDebugBlurAllFaces(enabled);
    setDebugBlurAllFaces(enabled);
  };

  const handleFaceBlurModeChange = (isServerMode: boolean) => {
    const mode: FaceBlurMode = isServerMode ? "server" : "client";
    broadcastStreamStore?.setFaceBlurMode(mode);
  };

  const generateMockUsers = () => {
    const count = parseInt(mockUserCount) || 0;
    if (count <= 0) return;

    const mockUsers = {};
    for (let i = 1; i <= count; i++) {
      const userId = `mock_user_${i}`;
      mockUsers[userId] = {
        id: userId,
        username: `MockUser${i}`,
        timeJoined: Date.now() - Math.random() * 3600000,
        registration_date: 1609459200000 + Math.random() * 31536000000,
        online: true,
        hasTokens: Math.random() > 0.3,
        balance: Math.floor(Math.random() * 5000),
        rank: i,
        spendingTier: Math.floor(Math.random() * 3) + 1,
        currentShowType: IShowType.FREE,
        isFanClub: Math.random() > 0.8,
        isPremiere: Math.random() > 0.9,
        isHighValue: Math.random() > 0.85,
        isBounty: Math.random() > 0.9,
      };
    }
    nodeChatStore!.allMemberHash = mockUsers;
  };

  const clearMockUsers = () => {
    nodeChatStore!.allMemberHash = {};
  };

  return (
    <TailwindFlex flexDirection='flex-col' gap='gap-4' padding={["p-4"]}>
      {/* Face Blur Debug Toggle */}
      <TailwindBox>
        <TailwindText
          fontSize='text-lg'
          fontWeight='font-bold'
          margin={["mb-2"]}>
          Face Blur Debug Options
        </TailwindText>
        <TailwindFlex alignItems='items-center' gap='gap-3' margin={["mb-2"]}>
          <TailwindSwitch
            label='Blur All Faces:'
            labelProps={{ fontWeight: "font-normal" }}
            isChecked={debugBlurAllFaces}
            onChange={handleDebugBlurAllFacesChange}
            size='sm'
            onLabel='ON'
            offLabel='OFF'
          />
        </TailwindFlex>
        <TailwindText
          fontSize='text-xs'
          textColor='text-gray-600'
          margin={["mb-3"]}>
          When enabled, all faces will be blurred (including your own) for
          testing. "Blur background faces" option in broadcast settings must
          also be on.
        </TailwindText>
        <TailwindFlex alignItems='items-center' gap='gap-3' margin={["mb-2"]}>
          <TailwindSwitch
            label='Blur Method:'
            labelProps={{ fontWeight: "font-normal" }}
            isChecked={broadcastStreamStore?.faceBlurMode === "server"}
            onChange={handleFaceBlurModeChange}
            size='xs-wide'
            onLabel='Server'
            offLabel='Client'
          />
        </TailwindFlex>
        <TailwindText fontSize='text-xs' textColor='text-gray-600'>
          Client: face-api.js runs in the browser. Server: blur server processes
          the stream on the backend.
        </TailwindText>
      </TailwindBox>

      {/* Broadcast ID Toggle */}
      <TailwindBox>
        <TailwindText
          fontSize='text-lg'
          fontWeight='font-bold'
          margin={["mb-2"]}>
          Broadcaster ID
        </TailwindText>
        <TailwindFlex alignItems='items-center' gap='gap-3' margin={["mb-2"]}>
          <TailwindDropdown
            name='idSource'
            label='ID Source:'
            labelPosition='left'
            labelProps={{ fontWeight: "font-normal", fontSize: "text-sm" }}
            items={[
              { label: "Stream ID", value: "stream" },
              { label: "Profile UUID", value: "profile" },
            ]}
            value={useProfileId ? "profile" : "stream"}
            onChange={handleIdSourceChange}
            width='w-40'
            backgroundColor='bg-white'
          />
        </TailwindFlex>
        <TailwindText
          fontSize='text-xs'
          textColor='text-gray-600'
          margin={["mb-1"]}>
          Stream ID (default): uses stream_id from /models/new/ API. Profile ID:
          uses profile.id (production behavior).
        </TailwindText>
        <TailwindText fontSize='text-xs' textColor='text-gray-600'>
          Current: {profileStore.broadcastId || "—"}
        </TailwindText>
      </TailwindBox>

      <TailwindBox>
        <TailwindText
          fontSize='text-lg'
          fontWeight='font-bold'
          margin={["mb-4"]}>
          Mock Users for Testing
        </TailwindText>
        <TailwindFlex flexDirection='flex-col' gap='gap-3'>
          <TailwindFlex alignItems='items-center' gap='gap-2'>
            <TailwindBox minWidth='min-w-40'>
              <TailwindText>Count:</TailwindText>
            </TailwindBox>
            <TailwindInput
              value={mockUserCount}
              onChange={e => setMockUserCount(e.target.value)}
              placeholder='Enter number of users'
              width='w-32'
            />
          </TailwindFlex>
          <TailwindFlex gap='gap-2'>
            <TailwindButton
              backgroundColor='primary'
              onClick={generateMockUsers}
              disabled={!mockUserCount || parseInt(mockUserCount) <= 0}>
              Generate Mock Users
            </TailwindButton>
            <TailwindButton
              backgroundColor='secondary'
              onClick={clearMockUsers}>
              Clear All Users
            </TailwindButton>
          </TailwindFlex>
          <TailwindText fontSize='text-sm' textColor='text-secondary-color'>
            Current users:{" "}
            {Object.keys(nodeChatStore?.allMemberHash || {}).length}
          </TailwindText>
        </TailwindFlex>
      </TailwindBox>

      {isRecording && (
        <TailwindBox>
          <TailwindText
            fontSize='text-lg'
            fontWeight='font-bold'
            margin={["mb-4"]}>
            Recording Controls
          </TailwindText>
          <TailwindFlex flexDirection='flex-col' gap='gap-3'>
            <TailwindText>
              Current Duration in Minutes:{" "}
              {Math.round(recordingDurationInSeconds / 60)}
            </TailwindText>
            <TailwindText>
              Remaining seconds left until it is full minute:{" "}
              {60 - (recordingDurationInSeconds % 60)}
            </TailwindText>
            <TailwindButton
              backgroundColor='primary'
              onClick={() => setRecordingDurationInSeconds(58 * 60)}>
              Set recording duration to 58th minute to see if it stops at end of
              59th minute
            </TailwindButton>
            <TailwindText fontSize='text-sm'>
              Note: recording stops at 59 minutes 59seconds
            </TailwindText>
          </TailwindFlex>
        </TailwindBox>
      )}
    </TailwindFlex>
  );
};

export default inject(
  "broadcastStore",
  "broadcastStreamStore",
  "nodeChatStore"
)(observer(QABroadcaster));
